import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import { loginAtom } from '../../../../lib/auth';
import { User } from '../../../../models/User';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';
import HelpersLanguage from '../../../../actions/helpers/language';

// Per-field render config: the i18n label key + how to read the value off the
// viewed client. Kept here (UI concern) rather than in the helper, which only
// owns data/logic — same split as DetailsContact's FIELD_RENDER. The whole
// widget renders for any client type, but second_name and call_name only apply
// to User::User (each field re-checks its gate here), matching the original.
type FieldRender = {
  labelKey: string;
  gate: (object: User) => boolean;
  value: (object: User) => string;
};

const FIELD_RENDER: Record<string, FieldRender> = {
  first_name: {
    labelKey: 'coach.models.clients.first_name',
    gate: () => true,
    value: (o) => o.first_name || '',
  },
  second_name: {
    labelKey: 'coach.models.clients.second_name',
    gate: (o) => o?.type === 'User::User',
    value: (o) => o.second_name || '',
  },
  last_name: {
    labelKey: 'coach.models.clients.last_name',
    gate: () => true,
    value: (o) => o.last_name || '',
  },
  call_name: {
    labelKey: 'coach.models.clients.call_name',
    gate: (o) => o?.type === 'User::User',
    value: (o) => o.call_name || '',
  },
  language: {
    labelKey: 'coach.models.clients.language',
    gate: () => true,
    // mapping_to_iso defaults to 'nl', so this is always non-empty — hide_empty
    // never hides the language row (matches original behaviour).
    value: (o) => HelpersLanguage.mapping_to_iso(o.language_id).toUpperCase(),
  },
};

type Props = {
  object: User;
}

export const CoachUserWidgetsDetailsBasic: FunctionComponent<Props> = ({
  object
}) => {
  const [login] = useAtom(loginAtom);
  const navigate = useNavigate();
  const {t} = useTranslation(['translations']);

  // Basic settings are a coach preference, stored alongside the widget layout
  // on login.settings.userdetails_widgets.settings. `details_basic` bundles
  // { fields, hide_empty, edit } under one composite value.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.detailsBasicValueKey] || {};
  const fields = cfg.fields || {};
  const hideEmpty = cfg.hide_empty !== false; // default on
  const editOn = cfg.edit !== false;          // default on

  // Fixed-order field items, each tagged with shown/hidden state.
  const ordered = HelpersClientWidgets.getOrderedDetailsBasicFields(fields);

  // Render only shown fields whose gate passes; when `hide_empty` is on, also
  // skip fields whose value is blank.
  const rows = ordered
    .filter((item) => item.state === 'shown')
    .map((item) => {
      const render = FIELD_RENDER[item.key];
      if (!render || !render.gate(object)) return null;
      const value = render.value(object);
      if (hideEmpty && !value) return null;
      return { key: item.key, labelKey: render.labelKey, value };
    })
    .filter(Boolean) as { key: string; labelKey: string; value: string }[];

  // Edit affordance gate: only real users with basic consent. Toggled by the
  // widget's `edit` setting (default on).
  const canEdit = editOn
    && object?.type === 'User::User'
    && !!object.consent_status?.is_basic;

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* Edit CTA: small text button top-right, absolute in the title row
        (space-saving — costs no extra vertical space). Same navigate target
        as before. */}
    {canEdit && <Button
      label={t("coach.general.actions.edit_basic")}
      id="edit_basic"
      text
      size="small"
      startIcon={<HelpersClientWidgets.detailsBasicEditIcon sx={{fontSize: '1.1rem'}} />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}`, {state: {detail: true}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.basic_information")}</Typography>
        <Divider />
      </Grid>
      {rows.map((row) => <Grid item xs={12} key={row.key}>
        <Typography variant="subtitle2" html>{t(row.labelKey)}</Typography>
        <Typography>{row.value}</Typography>
      </Grid>)}
    </Grid>
  </Paper>;
}
